/**
 * EmergencyContactsSection — list + add/edit/remove + save for a
 * patient's emergency contacts.
 *
 * Designed to drop into both the Contact Details tab and the
 * Community tab (Flutter `emergency_contacts_widget.dart`). Owns its
 * own server state via `useEmergencyContacts` and ships the full list
 * via `useSaveEmergencyContacts` — atomic save (Flutter parity, not
 * per-row deltas).
 *
 * Save is intentionally a SEPARATE button from the tab's "Save patient"
 * action. The Flutter app does the same: emergency contacts have their
 * own sub-resource endpoint (`/Patients/EmergencyContact`) and are
 * persisted independently of the patient body. Keeping the save here
 * avoids coupling the patient-save flow to a sub-resource the user
 * may not have touched.
 *
 * Primary-emergency-contact invariant:
 *   - At most one non-deleted row can have `isProxy=true`
 *   - Toggling a row to proxy demotes any other row currently set
 *   - When the proxy is removed, no auto-promotion (Flutter parity —
 *     user must pick the next primary)
 */

import { useEffect, useState } from 'react';
import { RecordListAdd, RecordListEmpty } from '@/shared/ui/components/record-row-card';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { toast } from 'sonner';
import {
  useEmergencyContacts,
  useSavePatientEmergencyContactList,
} from '@/patients/services/use-emergency-contacts';
import {
  emptyContactRow,
  type EmergencyContactRow,
} from './emergency-contact-schema';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import { EmergencyContactRowComponent } from './emergency-contact-row';
import type { EmergencyContactList } from '@/types/patient-api';
import { useUnsavedChangesSource } from '@/shared/ui/components/unsaved-changes';

export interface EmergencyContactsSectionProps {
  patientId: string | null | undefined;
  readOnly?: boolean;
}

export function EmergencyContactsSection({
  patientId,
  readOnly,
}: EmergencyContactsSectionProps) {
  // PROD-2743 · switch ON: the DS `add` glyph (one icon system, 16px inline); OFF = today's lucide.
  const { data: serverList, isLoading } = useEmergencyContacts(patientId);
  const saveMutation = useSavePatientEmergencyContactList();
  const [rows, setRows] = useState<EmergencyContactRow[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  // Hydrate local rows whenever the server snapshot changes (initial
  // load + post-save refetch).
  useEffect(() => {
    // Array.isArray, not a truthiness check: a non-list payload in this cache used to take the
    // whole app down here rather than degrading this one section (PROD-1988). The save hook no
    // longer writes one, but the consumer should not be the thing that decides that.
    if (!Array.isArray(serverList)) return;
    setRows(serverList.map(rowFromServer));
    setIsDirty(false);
  }, [serverList]);

  const liveRowCount = rows.filter((r) => !r.isDeleted).length;

  const handleAdd = () => {
    if (readOnly) return;
    const next = emptyContactRow();
    // First row added becomes the primary by default.
    if (liveRowCount === 0) next.isProxy = true;
    setRows((prev) => [...prev, next]);
    setIsDirty(true);
  };

  const handleRowChange = (i: number, next: EmergencyContactRow) => {
    setRows((prev) => {
      // Maintain the at-most-one-proxy invariant.
      const wasProxy = prev[i]?.isProxy ?? false;
      const isPromoting = next.isProxy && !wasProxy;
      return prev.map((r, idx) => {
        if (idx === i) return next;
        if (isPromoting) return { ...r, isProxy: false };
        return r;
      });
    });
    setIsDirty(true);
  };

  const handleCancel = (i: number) => {
    setRows((prev) => {
      const row = prev[i];
      if (!row) return prev;
      if (!row.emergencyContactId) {
        // Newly-added row → splice out entirely.
        return prev.filter((_, idx) => idx !== i);
      }
      // Existing row → exit edit mode, restore from server snapshot.
      const snapshot = (serverList ?? []).find(
        (s) => s.emergencyContactId === row.emergencyContactId,
      );
      if (snapshot) {
        return prev.map((r, idx) => (idx === i ? rowFromServer(snapshot) : r));
      }
      return prev.map((r, idx) => (idx === i ? { ...r, isEdit: false } : r));
    });
  };

  const handleRemove = (i: number) => {
    if (readOnly) return;
    setRows((prev) => {
      const row = prev[i];
      if (!row) return prev;
      if (!row.emergencyContactId) {
        // Never saved → splice.
        return prev.filter((_, idx) => idx !== i);
      }
      // Soft-delete. No auto-promotion if the deleted row was the proxy —
      // Flutter forces the user to manually pick the next primary.
      return prev.map((r, idx) =>
        idx === i ? { ...r, isDeleted: true, isEdit: false } : r,
      );
    });
    setIsDirty(true);
  };

  const handleSave = async (): Promise<boolean> => {
    if (!patientId) {
      toast.error('Cannot save — patient not loaded yet.');
      return false;
    }
    if (rows.some((r) => r.isEdit)) {
      toast.error('Finish editing rows before saving.');
      return false;
    }
    try {
      await saveMutation.mutateAsync({
        patientId,
        contacts: rows.map((row) => rowToServer(row, patientId)),
      });
      toast.success('Emergency contacts saved');
      setIsDirty(false);
      return true;
    } catch {
      // Global error handler surfaces server errors via the QueryClient onError.
      return false;
    }
  };

  // Cancel = back to the last saved list (the server snapshot the rows were hydrated from).
  const handleDiscard = () => {
    if (Array.isArray(serverList)) setRows(serverList.map(rowFromServer));
    setIsDirty(false);
  };

  // Inside a page that owns the "Unsaved changes" bar, that bar saves and cancels; this section's own Save hides.
  const hosted = useUnsavedChangesSource({
    dirty: isDirty,
    saving: saveMutation.isPending,
    save: handleSave,
    cancel: handleDiscard,
  });

  const visibleRows = rows
    .map((row, i) => ({ row, i }))
    .filter(({ row }) => !row.isDeleted);

  if (!patientId) {
    return (
      <RecordListEmpty>Save the patient first before adding emergency contacts.</RecordListEmpty>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  return (
    // The approved design's PaRecordList: the rows 12 apart, the empty line, the "Add …" row at the foot.
    <div data-slot="record-list">
      {visibleRows.length === 0 ? (
        <>
          <RecordListEmpty>No emergency contacts on file.</RecordListEmpty>
          {!readOnly ? <RecordListAdd onClick={handleAdd}>Add emergency contact</RecordListAdd> : null}
        </>
      ) : (
        <>
          {visibleRows.map(({ row, i }) => (
            <EmergencyContactRowComponent
              key={row.emergencyContactId || `new-${i}`}
              row={row}
              isOnly={visibleRows.length <= 1}
              readOnly={readOnly}
              onChange={(next) => handleRowChange(i, next)}
              onCancel={() => handleCancel(i)}
              onRemove={() => handleRemove(i)}
            />
          ))}
          {!readOnly ? (
            <RecordListAdd onClick={handleAdd}>Add emergency contact</RecordListAdd>
          ) : null}
        </>
      )}

      {!readOnly && !hosted ? (
        <div className="flex justify-end pt-2">
          <Button
            type="button"
            size="sm"
            onClick={() => void handleSave()}
            disabled={!isDirty || saveMutation.isPending || rows.some((r) => r.isEdit)}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save emergency contacts'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ── Projection helpers ────────────────────────────────────────────────────

// Exported so the appointment-context `EditCallBackContactView` can
// reuse the same server↔row projection. Both hosts ship through the
// same POST /api/Patients/EmergencyContact endpoint so the wire shape
// is identical.
export function rowFromServer(s: EmergencyContactList): EmergencyContactRow {
  // GET `/api/Patients/EmergencyContactList/<id>` ships `relation` with
  // PascalCase keys (`{Id, Name}`) — same Coding-shape inconsistency as
  // `/api/Master/Relation`. Normalize on read so the rest of the UI sees
  // lowercase `{id, name}` regardless of the backend's casing choice.
  // Verified against live GET 2026-05-15.
  const relRaw = (s.relation ?? null) as
    | { id?: string | null; name?: string | null; Id?: string | null; Name?: string | null }
    | null;
  return {
    emergencyContactId: s.emergencyContactId ?? '',
    firstName: s.firstName ?? '',
    middleName: s.middleName ?? null,
    lastName: s.lastName ?? '',
    contactNumber: (s.contactNumber ?? '').trim(),
    relation: {
      id: relRaw?.Id ?? relRaw?.id ?? null,
      name: relRaw?.Name ?? relRaw?.name ?? null,
    },
    relationship:
      s.relationship ?? relRaw?.Name ?? relRaw?.name ?? null,
    isProxy: s.isProxy ?? false,
    // Address block (F-PAT-242) — hydrate from the server snapshot.
    address: s.address ?? null,
    apartment: s.apartment ?? null,
    city: s.city ?? null,
    country: s.country ?? null,
    latitude: s.latitude ?? null,
    longitude: s.longitude ?? null,
    isDeleted: s.isDeleted ?? false,
    isEdit: false,
  };
}

/**
 * Build the wire row. Verified against live POST curl
 * `/api/Patients/EmergencyContact?PatientId=<id>` (2026-05-14).
 *
 * **Critical wire quirks** — got this wrong in the first cut of
 * PROD-729; corrected here per Samer's review on PR #87:
 *   - `relationship` is the **GUID** of the relation (NOT the name).
 *     The earlier version sent the name string — backend silently
 *     failed to resolve.
 *   - `relation` ships with **PascalCase keys** (`Id`, `Name`, `System`,
 *     `Code`). Lowercase fails backend deserialisation. `System` and
 *     `Code` ship as `null` when the master record doesn't carry them
 *     (the dev curl always populates them; nulling is the safer
 *     default for client-constructed new rows).
 *   - **Null-padded address block** — every row carries `photoUrl`,
 *     `building`, `apartment`, `country`, `city`, `address`, `latitude`,
 *     `longitude`, `patientId` (all `null` when blank). The .NET model
 *     binder treats missing fields as malformed, not optional.
 *   - `emergencyContactId` is OMITTED entirely for new rows. Verified
 *     against live POST 400s on 2026-05-15: the .NET model binder rejects
 *     both `""` (`Error converting value "" to type 'System.Guid'`) and
 *     `null` (`Error converting value {null} to type 'System.Guid'`),
 *     because the bound property is a non-nullable `Guid`. Omitting the
 *     field lets the binder pick the default — backend assigns the
 *     server-side GUID. Existing rows ship their GUID unchanged.
 *   - `submittedDate` set to now (no-Z millis form) on new rows; null
 *     for existing rows so we don't overwrite the audit stamp.
 */
// Exported for reuse — see note on `rowFromServer`.
export function rowToServer(
  row: EmergencyContactRow,
  patientId: string,
): EmergencyContactList {
  const isNew = !row.emergencyContactId;
  // PascalCase relation envelope — backend expects Id/Name/System/Code.
  // We cast through `unknown` because the IdNameModel type uses
  // lowercase {id, name}; the PascalCase shape is wire-specific.
  const relationOut =
    row.relation.id || row.relation.name
      ? ({
          Id: row.relation.id ?? null,
          Name: row.relation.name ?? null,
          System: null,
          Code: null,
        } as unknown as EmergencyContactList['relation'])
      : null;

  const base: EmergencyContactList = {
    patientId,
    photoUrl: null,
    // `relationship` is the GUID, NOT the name. Existing rows already
    // carry the GUID; new rows resolve from `row.relation.id`.
    relationship: row.relationship && row.relationship.includes('-')
      ? row.relationship
      : row.relation.id ?? null,
    firstName: row.firstName,
    lastName: row.lastName,
    middleName: row.middleName,
    contactNumber: row.contactNumber,
    isProxy: row.isProxy,
    isDeleted: row.isDeleted,
    relation: relationOut,
    submittedDate: isNew ? nowLocalIso() : null,
    // Address block (F-PAT-242) — now collected by the form. `building`
    // stays null (the Flutter EC widget doesn't collect it either);
    // backend requires every key present, so null-pad the rest.
    //
    // SINGLE SOURCE OF TRUTH (Samer review #488): the section saves via
    // `useSavePatientEmergencyContactList` (use-emergency-contacts.ts), which
    // POSTs this `EmergencyContactList[]` body DIRECTLY — no second field
    // mapping/whitelisting layer. (The separate `use-save-emergency-contacts.ts`
    // DevContactRow mapper is the PROD-715-era *scheduling* hook and is NOT on
    // this patients-chart save path.) So there is no double-null-pad: whatever
    // this function sets is exactly what ships. Wire shape curl-verified
    // 2026-05-14 — see this function's docblock.
    building: null,
    apartment: row.apartment ?? null,
    country: row.country ?? null,
    city: row.city ?? null,
    address: row.address ?? null,
    latitude: row.latitude ?? null,
    longitude: row.longitude ?? null,
  };
  // Omit emergencyContactId entirely on new rows; backend assigns the GUID.
  return isNew
    ? base
    : { ...base, emergencyContactId: row.emergencyContactId };
}

function nowLocalIso(): string {
  // YYYY-MM-DDTHH:mm:ss.sss (no `Z`) — Flutter wire convention.
  //
  // PROD-1778: this was `new Date().toISOString().replace(/Z$/, '')`, which
  // made the name a lie. `toISOString()` converts to UTC before the strip, so
  // the result is UTC wall-clock with the `Z` removed — the backend binds the
  // literal, so a contact added at 13:00 Asia/Dubai got a 09:00 audit stamp.
  // Now genuinely local.
  return formatLocalDatetimeForWire(new Date());
}
